import React, { FC, useEffect, useMemo, useState } from 'react';
import {
  fmt1,
  fmtInt,
  KIND_LABEL,
  KINDS,
  projectCapacity,
} from './opsCapacityUtils';
import type {
  OpsCapacityAssumptions,
  OpsCapacityKind,
  OpsCapacityRates,
} from './types';
import { Empty, SimpleTable } from './OpsCapacitySection';

interface Props {
  rates: OpsCapacityRates;
  defaults: OpsCapacityAssumptions;
}

const NumberField: FC<{
  label: string;
  value: number;
  step?: number;
  min?: number;
  onChange: (v: number) => void;
}> = ({ label, value, step = 0.1, min = 0, onChange }) => (
  <label className='ops-field'>
    <span>{label}</span>
    <input
      type='number'
      inputMode='decimal'
      value={Number.isFinite(value) ? value : ''}
      step={step}
      min={min}
      onChange={(e) => {
        const v = parseFloat(e.target.value);
        onChange(Number.isFinite(v) ? Math.max(min, v) : min);
      }}
    />
  </label>
);

/**
 * Staffing projections at the fixed WAU targets plus the "% of registered
 * users active" scenarios. Every input is editable so an ops lead can test
 * their own handle-time or shift-length assumptions without a code change;
 * edits are local to the browser tab and reset with the button.
 */
export const ProjectionsSection: FC<Props> = ({ rates, defaults }) => {
  const [assumptions, setAssumptions] =
    useState<OpsCapacityAssumptions>(defaults);
  useEffect(() => setAssumptions(defaults), [defaults]);

  const setHandle = (k: OpsCapacityKind, v: number) =>
    setAssumptions((a) => ({
      ...a,
      handleMinutes: { ...a.handleMinutes, [k]: v },
    }));

  const scenarios = useMemo(() => {
    const targets = assumptions.wauTargets.map((wau) => ({
      label: `${fmtInt(wau)} WAU`,
      wau,
    }));
    const shares = assumptions.activeSharePcts.map((pct) => ({
      label: `${pct}% of ${fmtInt(assumptions.registeredUsers)} registered`,
      wau: Math.round((assumptions.registeredUsers * pct) / 100),
    }));
    return [...targets, ...shares].map((s) => ({
      ...s,
      projection: projectCapacity(rates, assumptions, s.wau),
    }));
  }, [rates, assumptions]);

  if (rates.weeksUsed === 0) {
    return (
      <Empty>
        Projections need at least one complete ISO week with active users inside
        the selected range.
      </Empty>
    );
  }

  const isDefault = JSON.stringify(assumptions) === JSON.stringify(defaults);

  return (
    <div className='ops-projections'>
      <div className='ops-rates'>
        <span className='ops-rates-title'>
          Observed demand per active user per week
        </span>
        <span className='ops-rates-meta'>
          averaged over {rates.weeksUsed} complete week
          {rates.weeksUsed === 1 ? '' : 's'} · {fmtInt(rates.wauWeeks)}{' '}
          user-weeks
        </span>
        <ul>
          {KINDS.map((k) => (
            <li key={k}>
              <strong>{rates[k].toFixed(3)}</strong>{' '}
              {KIND_LABEL[k].toLowerCase()}
            </li>
          ))}
        </ul>
      </div>

      <form
        className='ops-assumptions'
        onSubmit={(e) => e.preventDefault()}
        aria-label='Projection assumptions'
      >
        <div className='ops-assumptions-head'>
          <h4>Assumptions (editable)</h4>
          <button
            type='button'
            className='ops-btn-link'
            disabled={isDefault}
            onClick={() => setAssumptions(defaults)}
          >
            Reset to defaults
          </button>
        </div>
        <div className='ops-assumptions-grid'>
          {KINDS.map((k) => (
            <NumberField
              key={k}
              label={`${KIND_LABEL[k]} — minutes each`}
              value={assumptions.handleMinutes[k]}
              onChange={(v) => setHandle(k, v)}
            />
          ))}
          <NumberField
            label='Productive hours per operator per week'
            value={assumptions.productiveHoursPerWeek}
            step={1}
            min={1}
            onChange={(v) =>
              setAssumptions((a) => ({ ...a, productiveHoursPerWeek: v }))
            }
          />
          <NumberField
            label='Peak day ÷ average day'
            value={assumptions.peakDayFactor}
            step={0.05}
            min={1}
            onChange={(v) =>
              setAssumptions((a) => ({ ...a, peakDayFactor: v }))
            }
          />
          <NumberField
            label='Registered users'
            value={assumptions.registeredUsers}
            step={1000}
            onChange={(v) =>
              setAssumptions((a) => ({ ...a, registeredUsers: Math.round(v) }))
            }
          />
        </div>
      </form>

      <SimpleTable
        columns={[
          { key: 'scenario', label: 'Scenario' },
          { key: 'wau', label: 'WAU', align: 'right' },
          ...KINDS.map((k) => ({
            key: k,
            label: `${KIND_LABEL[k]} / wk`,
            align: 'right' as const,
          })),
          { key: 'hours', label: 'Operator h / wk', align: 'right' },
          { key: 'fte', label: 'Avg FTE', align: 'right' },
          { key: 'peak', label: 'Peak-day FTE', align: 'right' },
        ]}
        rows={scenarios.map((s) => ({
          scenario: s.label,
          wau: fmtInt(s.wau),
          ...Object.fromEntries(
            KINDS.map((k) => [k, fmtInt(s.projection.items[k])]),
          ),
          hours: fmtInt(Math.round(s.projection.operatorHoursPerWeek)),
          fte: fmt1(s.projection.averageFte),
          peak: fmt1(s.projection.peakDayFte),
        }))}
        rowKey={(r) => String(r.scenario)}
      />
    </div>
  );
};
